import { boundaryLoopOfFaceSet } from '../boundary.js';
import { edgeId, faceNormal, propagateExplicitEdgeIds } from '../edges.js';
import { coplanarRegion } from '../regions.js';
import { dot3 } from '../vector-math.js';

// A displacement counts as "in the region's plane" (and is rejected) once
// the ratio of its normal component to its own length drops below this —
// scale-invariant on purpose, so a long near-in-plane displacement is
// rejected exactly as readily as a short one at the same angle. Same
// tightness rationale as REGION_NORMAL_EPSILON/INSET_NORMAL_EPSILON: this is
// about recognizing a displacement that is genuinely (or all-but-)
// in-plane, not a generous "roughly oblique enough" allowance.
const EXTRUDE_PLANAR_EPSILON = 1e-6;

/**
 * The one extrusion algorithm — extrude an explicit, caller-chosen *set* of
 * faces as a single region, in place. `extrudeFace` (below) is the
 * single-face case (`faceIds.length === 1`); `extrudeRegion` discovers a
 * connected coplanar region and passes its face ids here. There is
 * deliberately no second implementation anywhere else — region discovery is
 * the only thing that differs between the two public operations, not the
 * geometry construction.
 *
 * Every vertex referenced by *any* face in the set is duplicated once
 * (offset by `displacement`, ids minted from `nextVertexId`) — including
 * vertices interior to the region, not just its boundary — and every source
 * face gets a translated copy using the duplicate mapping, in the same
 * per-triangle vertex order (so the cap's own winding matches the source's,
 * face for face; a coplanar region's normal is identical across all its
 * faces by definition, so this is consistent across the whole cap). Side
 * walls are built only along the region's *outer* boundary: an edge shared
 * by two faces *within* the set is internal and gets no wall, which is
 * exactly what fixes the single-triangle case's "one visible box face is
 * actually two triangles, so extruding one alone leaves a pinched wedge"
 * problem — extruding both of a quad's triangles as one region removes the
 * shared diagonal from wall consideration entirely.
 *
 * Boundary detection: walk each face's 3 edges in its own vertex order,
 * collecting every directed (a, b) edge across the whole set. Two triangles
 * sharing an edge always traverse it in *opposite* directions if both are
 * consistently CCW-wound (a basic manifold-mesh invariant) — so a directed
 * edge is internal exactly when its reverse also appears in the set, and
 * boundary otherwise. The surviving boundary edges are chained head-to-tail
 * into one ordered loop; if the region doesn't form a single simple loop
 * (disconnected faces, a hole, a branch), that chaining fails and throws —
 * this function assumes a disk-shaped patch, which is what `coplanarRegion`
 * always discovers, but is validated rather than assumed blindly.
 *
 * Winding: cap and wall faces are built directly in the source faces' own
 * vertex order, with no reversal for either sign of the displacement's
 * normal component. For boundary edge `(a, b)` (walked in the source
 * region's own winding direction), the untouched neighboring face outside
 * the region — if any — necessarily traverses that same edge as `(b, a)`
 * (two triangles sharing an edge always traverse it in opposite directions
 * in a consistently-wound manifold mesh, the same invariant the boundary
 * detection below relies on). The wall built here as `[a, b, bTop]` /
 * `[a, bTop, aTop]` traverses `(a, b)` — matching the source region and
 * staying orientation-consistent with that untouched neighbor — for *both* a
 * positive and a negative displacement along the region's normal: which
 * side of the source plane the new geometry lands on changes, but the
 * *direction* boundary edges must be walked to stay consistent with the rest
 * of the mesh does not. (An earlier version of this function reversed every
 * new face's winding when `dot(referenceNormal, displacement) < 0` — this
 * broke exactly that boundary-edge invariant for a negative-normal
 * extrusion, inverting the new shell relative to the untouched mesh around
 * it; see the extrude design doc's "Coplanar region extrusion" section for
 * the corrected account.)
 *
 * Separately validated below (see `EXTRUDE_PLANAR_EPSILON`): a displacement
 * with no meaningful component along `referenceNormal` is rejected outright,
 * since a displacement confined to (or nearly confined to) the region's own
 * plane sweeps no volume — there is no winding that produces valid geometry
 * for it, so that is a validation question, not a winding one.
 *
 * All-or-nothing: every lookup, validation, and boundary computation happens
 * before anything is written to `topology`.
 */
function extrudeFaceSet(topology, faceIds, displacement, commandName = 'extrudeFaceSet') {
  if (!Array.isArray(faceIds) || faceIds.length === 0) {
    throw new Error(`${commandName} requires a non-empty array of face ids`);
  }
  const uniqueFaceIds = new Set(faceIds);
  if (uniqueFaceIds.size !== faceIds.length) {
    throw new Error(`${commandName} received duplicate face ids`);
  }
  // Existence checked first, one at a time in call order, so the *first*
  // problem reported is always "unknown face id" rather than an unrelated
  // displacement error — same actionable-error-first rule extrudeFace has
  // always followed for its single id.
  for (const id of faceIds) {
    if (!topology.faces.some((f) => f.id === id)) throw new Error(`unknown face id '${id}'`);
  }

  if (!Array.isArray(displacement) || displacement.length !== 3 || !displacement.every(Number.isFinite)) {
    throw new Error(`${commandName} requires a finite [dx, dy, dz] displacement`);
  }
  const displacementLength = Math.hypot(...displacement);
  if (displacementLength < 1e-9) {
    throw new Error(`${commandName} requires a non-zero displacement`);
  }

  // Deterministic region order: the topology's own existing face array
  // order, filtered to the set — for a single face this is trivially just
  // that face, so extrudeFace's output is byte-identical to before.
  const faces = topology.faces.filter((f) => uniqueFaceIds.has(f.id));

  // Every member face must still be a valid, non-degenerate triangle before
  // anything is written — also doubles as the up-front lookup that would
  // throw on a face referencing an unknown vertex. Capture the first face's
  // normal as the region's reference normal instead of recomputing it.
  let referenceNormal;
  for (const face of faces) {
    const normal = faceNormal(topology, face.id);
    if (!referenceNormal) referenceNormal = normal;
  }

  // A displacement (nearly) confined to the region's own plane sweeps no
  // volume: the cap would land back in the source plane and the walls would
  // collapse toward zero area — half of them facing backwards, since the
  // in-plane component of a boundary edge's wall normal flips sign around
  // the loop (see EXTRUDE_PLANAR_EPSILON). Checked as a *ratio* of the
  // normal component to the displacement's own length, not against the
  // length alone — a long displacement that is merely close to in-plane is
  // exactly as degenerate as a short one at the same angle, and the earlier
  // "non-zero displacement" check above only guards against a literally
  // zero vector, not this.
  const normalComponent = dot3(referenceNormal, displacement);
  if (Math.abs(normalComponent) < EXTRUDE_PLANAR_EPSILON * displacementLength) {
    throw new Error(`${commandName} requires a displacement with a component along the region's normal (a displacement in the region's own plane, or parallel to one of its boundary edges, sweeps no volume)`);
  }

  // Region vertex order: first-appearance order while walking `faces` in the
  // order above — for a single face this is exactly that face's own vertex
  // order, matching extrudeFace's previous, directly-observable output.
  // A plain Set preserves insertion order, so this is just its dedup.
  const regionVertexOrder = [...new Set(faces.flatMap((face) => face.vertexIds))];

  const positionById = new Map(topology.vertices.map((v) => [v.id, v.position]));
  let nextVertexId = topology.nextVertexId;
  const duplicateOf = new Map();
  const newVertices = regionVertexOrder.map((oldId) => {
    const newId = `v${nextVertexId++}`;
    duplicateOf.set(oldId, newId);
    const position = positionById.get(oldId);
    return { id: newId, position: [position[0] + displacement[0], position[1] + displacement[1], position[2] + displacement[2]] };
  });

  let nextFaceId = topology.nextFaceId;
  const capFaces = faces.map((face) => ({
    id: `f${nextFaceId++}`,
    vertexIds: face.vertexIds.map((vertexId) => duplicateOf.get(vertexId)),
  }));

  const sourceEdges = new Map();
  for (const face of faces) {
    for (let i = 0; i < face.vertexIds.length; i++) {
      const a = face.vertexIds[i];
      const b = face.vertexIds[(i + 1) % face.vertexIds.length];
      const id = edgeId(a, b);
      const entry = sourceEdges.get(id);
      if (entry) entry.count++;
      else sourceEdges.set(id, { a, b, count: 1 });
    }
  }
  const explicitEdgeReplacements = new Map();
  for (const [id, { a, b, count }] of sourceEdges) {
    if (count > 1) {
      explicitEdgeReplacements.set(id, [edgeId(duplicateOf.get(a), duplicateOf.get(b))]);
    }
  }
  const explicitEdgeIds = propagateExplicitEdgeIds(topology.explicitEdgeIds, explicitEdgeReplacements);

  const boundaryLoop = boundaryLoopOfFaceSet(faces, commandName);

  const wallFaces = [];
  for (let i = 0; i < boundaryLoop.length; i++) {
    const a = boundaryLoop[i];
    const b = boundaryLoop[(i + 1) % boundaryLoop.length];
    const aTop = duplicateOf.get(a);
    const bTop = duplicateOf.get(b);
    wallFaces.push({ id: `f${nextFaceId++}`, vertexIds: [a, b, bTop] });
    wallFaces.push({ id: `f${nextFaceId++}`, vertexIds: [a, bTop, aTop] });
  }

  topology.vertices.push(...newVertices);
  topology.faces = topology.faces.filter((f) => !uniqueFaceIds.has(f.id));
  topology.faces.push(...capFaces, ...wallFaces);
  topology.nextVertexId = nextVertexId;
  topology.nextFaceId = nextFaceId;
  if (explicitEdgeIds !== undefined) topology.explicitEdgeIds = explicitEdgeIds;

  return {
    removedFaceIds: faces.map((f) => f.id),
    capFaceIds: capFaces.map((f) => f.id),
    newVertexIds: newVertices.map((v) => v.id),
    newFaceIds: [...capFaces, ...wallFaces].map((f) => f.id),
  };
}

/**
 * Extrude one triangular face by a displacement vector, in place. See
 * `extrudeFaceSet` for the actual algorithm — this is the single-face case,
 * kept as its own export because most callers (including every existing
 * agent/CLI use) address one specific triangle and expect a single
 * `removedFaceId`/`capFaceId` back, not arrays. For "extrude the visible
 * face this triangle is part of," see `extrudeRegion`.
 */
export function extrudeFace(topology, faceId, displacement) {
  const outcome = extrudeFaceSet(topology, [faceId], displacement, 'extrudeFace');
  return {
    removedFaceId: outcome.removedFaceIds[0],
    capFaceId: outcome.capFaceIds[0],
    newVertexIds: outcome.newVertexIds,
    newFaceIds: outcome.newFaceIds,
  };
}

/**
 * Extrude the connected coplanar region containing `faceId` — the visible
 * face a human clicked on, not just the one triangle the click happened to
 * hit. See `coplanarRegion` for discovery and `extrudeFaceSet` for the
 * shared extrusion algorithm; this is a thin composition of the two, so
 * there is exactly one extrusion implementation, not two.
 */
export function extrudeRegion(topology, faceId, displacement, options) {
  const regionFaceIds = coplanarRegion(topology, faceId, options);
  const outcome = extrudeFaceSet(topology, regionFaceIds, displacement, 'extrudeRegion');
  return {
    removedFaceIds: outcome.removedFaceIds,
    capFaceIds: outcome.capFaceIds,
    // Deterministic representative for selection/chaining — meshSelection
    // (see mesh-selection-state.js) only ever needs one id to seed the next
    // extrude, since coplanarRegion rediscovers the whole new cap region
    // from any single face inside it.
    capFaceId: outcome.capFaceIds[0],
    newVertexIds: outcome.newVertexIds,
    newFaceIds: outcome.newFaceIds,
  };
}
